// Pure helpers for authored-pose identity and list maintenance. Kept out of
// CharacterStudio.tsx so the id logic is unit-testable without pulling in the
// R3F/three render tree.

import type { CharacterPose } from '../content/schema/character';

/** Slugify a pose name to the stable portion of its id. */
export function slugifyPoseName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** The base id an authored pose gets from its rig and name. */
export function makePoseId(rigId: string, name: string): string {
  return `authored-${rigId}-${slugifyPoseName(name)}`;
}

/**
 * Build a pose id unique among `existingIds`. Two distinct names that slugify
 * identically (e.g. "Guard Stance" vs "Guard  Stance!") would otherwise map to
 * the same id and silently overwrite one another; disambiguate with a numeric
 * suffix so both survive.
 */
export function makeUniquePoseId(rigId: string, name: string, existingIds: Iterable<string>): string {
  const taken = new Set(existingIds);
  const base = makePoseId(rigId, name);
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * Fold freshly saved poses into the list already on screen.
 *
 * `PosePersistence.savePoses` is an upsert of the poses it was handed, and both
 * shipped transports resolve with exactly that argument rather than the
 * project's full pose set. Assigning the result straight into state therefore
 * collapsed the authored list to whichever single pose was just saved, and the
 * rest only reappeared on reload. Merge by id instead: later wins, first
 * appearance sets the order, so a re-saved pose updates in place rather than
 * jumping to the end.
 */
export function mergePosesById(existing: CharacterPose[], incoming: CharacterPose[]): CharacterPose[] {
  const byId = new Map<string, CharacterPose>();
  for (const pose of existing) byId.set(pose.id, pose);
  for (const pose of incoming) byId.set(pose.id, pose);
  return [...byId.values()];
}
